import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import * as React from 'react';
import { PropsWithChildren } from 'react';
import { Drawer as DrawerPrimitive } from 'vaul';

import { useWindowSize } from '../../../hooks/use-window-size';
import { cn } from '../../../libs/utils';
import { BaseOverlay } from '../overlay/Overlay';

const DrawerPortal = DrawerPrimitive.Portal;

const Drawer = ({ shouldScaleBackground = true, ...props }: React.ComponentProps<typeof DrawerPrimitive.Root>) => (
  <DrawerPrimitive.Root repositionInputs={false} shouldScaleBackground={shouldScaleBackground} {...props} />
);

const DrawerOverlay = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Overlay> & {
    outSideClickAction?: () => void;
  }
>(({ className, outSideClickAction, ...props }, ref) => {
  return (
    <DrawerPrimitive.Overlay ref={ref} asChild {...props}>
      <BaseOverlay
        outSideClickAction={outSideClickAction}
        className={cn(
          'z-overlay-drawer data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
          className
        )}
      />
    </DrawerPrimitive.Overlay>
  );
});
DrawerOverlay.displayName = DrawerPrimitive.Overlay?.displayName;

interface DrawerContentProps {
  disableOutsideClick?: boolean;
  isFullScreen?: boolean;
  outSideClickAction?: () => void;
}

const DrawerContent = ({ children, disableOutsideClick, isFullScreen, outSideClickAction }: PropsWithChildren<DrawerContentProps>) => {
  const { windowHeight } = useWindowSize();

  return (
    <DrawerPortal>
      <DrawerOverlay outSideClickAction={outSideClickAction} />
      <div className={'flex justify-center'}>
        <DrawerPrimitive.Content
          aria-describedby={undefined}
          style={{
            height: isFullScreen ? windowHeight : '',
            maxHeight: isFullScreen ? windowHeight : ''
          }}
          className={cn('container-medium fixed bottom-0 z-drawer flex max-h-[90%] w-full flex-col bg-neutral-background-main outline-none', {
            'rounded-none': isFullScreen,
            'rounded-t-bzc-xlarge': !isFullScreen
          })}
          onInteractOutside={disableOutsideClick ? (e) => e.preventDefault() : () => {}}
        >
          <VisuallyHidden>
            <DrawerPrimitive.Title />
          </VisuallyHidden>
          {children}
        </DrawerPrimitive.Content>
      </div>
    </DrawerPortal>
  );
};

const DrawerBody = ({ children, className, ...props }: PropsWithChildren<React.HTMLAttributes<HTMLDivElement>>) => {
  return (
    <div className={cn('flex flex-col overflow-auto px-normal-large pt-normal-small pb-normal-large', className)} {...props}>
      {children}
    </div>
  );
};

const DrawerFooter = ({ children, className, ...props }: PropsWithChildren<React.HTMLAttributes<HTMLDivElement>>) => {
  return (
    <div className={cn(className)} {...props}>
      {children}
    </div>
  );
};

const DrawerHeader = ({ children, className, ...props }: PropsWithChildren<React.HTMLAttributes<HTMLDivElement>>) => {
  return (
    <div className={cn('px-normal-large pb-normal-small pt-normal-large', className)} {...props}>
      {children}
    </div>
  );
};

export { Drawer, DrawerContent, DrawerFooter, DrawerHeader, DrawerBody };
